import React, { useState, useEffect } from 'react'
import QRCode from 'react-qr-code'
import {
  generateOTPAuthUrl,
  generateSecret,
  generateTOTP,
  generateRecoveryCodes
} from '../../hooks/totpUtils'
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD'

const MFAEnable = ({ user, onClose }) => {
  const [secret, setSecret] = useState(null)
  const [otp, setOtp] = useState('')
  const [verified, setVerified] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState([])
  const [error, setError] = useState('')

  const { updateDocument, loading, error: firebaseError } = useFirebaseCRUD()
  useEffect(() => {
    if (!secret) {
      const newSecret = generateSecret()
      setSecret({
        base32: newSecret,
        otpauth_url: generateOTPAuthUrl(newSecret, user.email, 'LiveAlarm-V2')
      })
    }
  }, [user.email])

  const verifyOtp = async () => {
    try {
      const generatedOtp = await generateTOTP(secret.base32)
      if (otp === generatedOtp) {
        const codes = generateRecoveryCodes()
        setRecoveryCodes(codes)
        setVerified(true)
        setError('')
      } else {
        setError('Invalid verification code. Please try again.')
      }
    } catch (err) {
      setError('Verification failed. Please try again.')
    }
  }

  const enableMFA = async () => {
    try {
      const success = await updateDocument(`users/${user.id}`, {
        isTwoFactorEnabled: true,
        secretKey: secret.base32,
        twoFactorRecoveryCodes: recoveryCodes,
        updatedAt: `${new Date().toISOString()}`
      })

      if (success) {
        onClose(true)
      } else {
        setError('Failed to enable MFA. Please try again.')
      }
    } catch (err) {
      console.error('Error enabling MFA:', err)
      setError('Failed to enable MFA. Please try again.')
    }
  }

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return // Only numbers allowed
    const newOtp = otp.split('')
    newOtp[index] = value
    const joinedOtp = newOtp.join('')
    setOtp(joinedOtp)
    setError('')

    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`).focus()
    }
  }

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus()
    }
  }

  if (!secret) return <div>Loading MFA setup...</div>

  return (
    <div className='p-4 max-w-md mx-auto'>
      {!verified ? (
        <>
          <h2 className='text-xl font-bold mb-4'>
            Enable Two-Factor Authentication
          </h2>
          <p className='mb-4'>Scan this QR code with your authenticator app:</p>
          <div className='mb-4 p-2 bg-white rounded'>
            <QRCode value={secret.otpauth_url} size={200} level='H' />
          </div>
          <p className='mb-2'>Or enter this secret key manually:</p>
          <code className='block mb-4 p-2 bg-gray-100 rounded break-all'>
            {secret.base32}
          </code>
          <div className='mb-4'>
            <label className='block mb-2'>Enter verification code:</label>
            <div className='flex gap-2 mb-4 justify-start'>
              {[...Array(6)].map((_, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type='text'
                  value={otp[index] || ''}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className='w-10 h-10 text-center text-lg border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500'
                  maxLength={1}
                  inputMode='numeric'
                  pattern='\d*'
                />
              ))}
            </div>
          </div>
          {(error || firebaseError) && (
            <p className='text-red-500 mb-4'>
              {error || firebaseError?.message}
            </p>
          )}
          <button
            onClick={verifyOtp}
            disabled={loading}
            className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600'>
            {loading ? 'Verifying...' : 'Verify Code'}
          </button>
        </>
      ) : (
        <>
          <h2 className='text-xl font-bold mb-4'>
            Two-Factor Authentication Enabled
          </h2>
          <p className='mb-4'>
            Keep these recovery codes in a safe place. You can use them to
            access your account if you lose your device.
          </p>
          <div className='mb-4 p-4 bg-gray-100 rounded'>
            {recoveryCodes.map((item, index) => (
              <div key={index} className='mb-1 font-mono'>
                {item.code}
              </div>
            ))}
          </div>
          <button
            onClick={enableMFA}
            disabled={loading}
            className='bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600'>
            {loading ? 'Enabling...' : 'Confirm and Enable MFA'}
          </button>
        </>
      )}
    </div>
  )
}

export default MFAEnable
